import { useState, Fragment } from "react";
import { Box, Typography, Popover } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { uiNeutrals } from "../../../constants/uiNeutrals";

export const DEALS_BROWSE_CALENDAR = "calendar";
export const DEALS_BROWSE_AVAILABLE = "available";
/** @deprecated use DEALS_BROWSE_AVAILABLE */
export const DEALS_BROWSE_THIS_WEEK = DEALS_BROWSE_AVAILABLE;
/** @deprecated removed — maps to available */
export const DEALS_BROWSE_NEXT_WEEK = "next_week";
/** @deprecated use DEALS_BROWSE_AVAILABLE */
export const DEALS_BROWSE_AVAILABILITY = DEALS_BROWSE_AVAILABLE;

const GLASSY_SHADOW_NORMAL =
  "0 8px 28px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.75)";
const GLASSY_SHADOW_ELEVATED =
  "0 10px 36px rgba(0,0,0,0.22), 0 3px 10px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.80)";

const OPTIONS = [
  {
    value: DEALS_BROWSE_CALENDAR,
    label: "Expiring",
    Icon: HourglassBottomIcon,
  },
  {
    value: DEALS_BROWSE_AVAILABLE,
    label: "Available",
    Icon: CalendarTodayIcon,
  },
];

const DealsBrowseBottomToggle = ({ mode, onModeChange, elevated = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const normalizedMode =
    mode === DEALS_BROWSE_NEXT_WEEK ||
    mode === DEALS_BROWSE_THIS_WEEK ||
    mode === DEALS_BROWSE_AVAILABILITY
      ? DEALS_BROWSE_AVAILABLE
      : mode;

  const current = OPTIONS.find((o) => o.value === normalizedMode) ?? OPTIONS[0];

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (value) => {
    onModeChange(value);
    handleClose();
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleOpen}
        onKeyDown={(e) => e.key === "Enter" && handleOpen(e)}
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "calc(40px + env(safe-area-inset-bottom, 0px))",
          zIndex: 1100,
          borderRadius: 999,
          px: 0.75,
          py: 0.45,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
          bgcolor: uiNeutrals.frostedFill,
          backdropFilter: "blur(24px) saturate(1.08)",
          WebkitBackdropFilter: "blur(24px) saturate(1.08)",
          border: "1px solid rgba(255, 255, 255, 0.55)",
          boxShadow: elevated ? GLASSY_SHADOW_ELEVATED : GLASSY_SHADOW_NORMAL,
          transition:
            "background-color 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.35,
            py: 0.65,
            minHeight: 34,
          }}
        >
          <current.Icon sx={{ fontSize: 16, color: "#2d2d2d" }} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.85rem",
              color: "#2d2d2d",
              lineHeight: 1,
            }}
          >
            {current.label}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              color: "#2d2d2d",
              transition: "transform 0.22s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              ml: 0.1,
            }}
          />
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: { offset: [0, 32] },
              },
            ],
          },
          paper: {
            elevation: 0,
            sx: {
              bgcolor: "rgba(253, 252, 251, 0.78)",
              backdropFilter: "blur(40px) saturate(1.8)",
              WebkitBackdropFilter: "blur(40px) saturate(1.8)",
              border: "1px solid rgba(255, 255, 255, 0.55)",
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.65)",
              borderRadius: "24px",
              overflow: "hidden",
              minWidth: 210,
              py: 1,
            },
          },
        }}
      >
        {OPTIONS.map((opt) => {
          const isActive = normalizedMode === opt.value;
          return (
            <Fragment key={opt.value}>
              <Box
                onClick={() => handleSelect(opt.value)}
                sx={{
                  px: 3.5,
                  py: 1.8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  justifyContent: "flex-start",
                  WebkitTapHighlightColor: "transparent",
                  "&:active": { bgcolor: "rgba(0,0,0,0.06)" },
                }}
              >
                <opt.Icon
                  sx={{
                    fontSize: 20,
                    color: isActive ? "#2d2d2d" : "#9a9a9a",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: isActive ? 600 : 500,
                    fontSize: "0.95rem",
                    color: isActive ? "#2d2d2d" : "#9a9a9a",
                    lineHeight: 1,
                  }}
                >
                  {opt.label}
                </Typography>
              </Box>
            </Fragment>
          );
        })}
      </Popover>
    </>
  );
};

export default DealsBrowseBottomToggle;
